import * as React from "react"
import { useCallback, useState } from "react"
import { Box, Chip, CircularProgress, IconButton, Typography } from "@mui/material"
import { ButtonSeeker, ModalSeeker, TextFieldSeeker } from "@ui-seeker/core"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"

import { BACKEND_APIS } from "@constants"
import { postSubmit } from "@ui-seeker/utils"


export default function KeywordTesterModal({ isOpen, onClose }) {
    const [candidates, setCandidates] = useState([""])
    const [results, setResults] = useState(null)
    const [loading, setLoading] = useState(false)

    const handleAddField = useCallback(() => {
        setCandidates(prev => [...prev, ""])
    }, [])

    const handleRemoveField = useCallback((index) => {
        setCandidates(prev => prev.filter((_, i) => i !== index))
        setResults(null)
    }, [])

    const handleChangeField = useCallback((index, value) => {
        setCandidates(prev => prev.map((c, i) => i === index ? value : c))
        setResults(null)
    }, [])

    const handleTest = useCallback(() => {
        const validCandidates = candidates.filter(c => c.trim())
        if (!validCandidates.length) return

        setLoading(true)
        setResults(null)

        postSubmit(`${BACKEND_APIS.BRANDS}/crud/test-keywords-global`, {
            candidates: validCandidates
        }).then(res => {
            setResults(res)
        }).catch(() => {
            setResults(null)
        }).finally(() => {
            setLoading(false)
        })
    }, [candidates])

    const handleClose = useCallback(() => {
        setCandidates([""])
        setResults(null)
        onClose()
    }, [onClose])

    return <ModalSeeker
        isOpen={isOpen}
        title="Keywords Tester"
        controller={handleClose}
        hideActions
        containerProps={{ maxWidth: "md" }}
    >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
                Enter product names to test which brand they match against using keyword rules.
            </Typography>

            {candidates.map((candidate, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextFieldSeeker
                        label={`Candidate ${index + 1}`}
                        placeholder="Enter a product name..."
                        value={candidate}
                        onChange={e => handleChangeField(index, e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleTest()}
                    />
                    {candidates.length > 1 && (
                        <IconButton size="small" onClick={() => handleRemoveField(index)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            ))}

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <ButtonSeeker variant="text" size="small" onClick={handleAddField} startIcon={<AddIcon />}>
                    Add candidate
                </ButtonSeeker>

                <ButtonSeeker
                    variant="contained"
                    onClick={handleTest}
                    disabled={loading || !candidates.some(c => c.trim())}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                >
                    {loading ? "Testing..." : "Test"}
                </ButtonSeeker>
            </Box>

            {results && <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Results</Typography>

                {results.map((r, index) => (
                    <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ minWidth: 200, fontWeight: 500 }}>
                            {r.candidate}
                        </Typography>
                        {r.matches.length
                            ? r.matches.map(m => (
                                <Chip
                                    key={m.brandID}
                                    label={`${m.brandName} (#${m.brandID})`}
                                    color="success"
                                    size="small"
                                    variant="outlined"
                                />
                            ))
                            : <Chip label="No match" color="error" size="small" variant="outlined" />
                        }
                    </Box>
                ))}
            </>}
        </Box>
    </ModalSeeker>
}
